if (!customElements.get('product-media-new-pdp')) {
  customElements.define(
    'product-media-new-pdp',
    class NewPdpCarousel extends HTMLElement {
      constructor() {
        super();

        this.childElements = [...this.children];
        this.childElements.forEach((child) => {
          child.classList.add('splide__slide');
        });
        this.slides = this.innerHTML;
        this.pagination = this.hasAttribute('pagination');
        this.useThumbnails = this.hasAttribute('thumbnails');

        this.generateCarousel = this.generateCarousel.bind(this);
        this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
        this.updateActiveState = this.updateActiveState.bind(this);
        this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
      }

      connectedCallback() {
        this.generateCarousel();
      }

      handleThumbnailClick(index) {
        // Update main image
        if (this.mainSplide) {
          this.mainSplide.go(index);
        }

        // Update active thumbnail state
        this.updateActiveState(index);
      }

      handleNavButtonClick(direction) {
        if (this.mainSplide) {
          if (direction === 'prev') {
            this.mainSplide.go('-1');
          } else {
            this.mainSplide.go('+1');
          }
        }
      }

      updateActiveState(index) {
        // Update desktop thumbnails active state
        if (this.desktopThumbnails) {
          const thumbnails =
            this.desktopThumbnails.querySelectorAll('.thumbnail-item');
          thumbnails.forEach((thumb, i) => {
            if (i === index) {
              thumb.classList.add('border-brand-secondary-200');
              thumb.classList.remove('border-transparent');
            } else {
              thumb.classList.remove('border-brand-secondary-200');
              thumb.classList.add('border-transparent');
            }
          });
        }

        // Update nav buttons active state
        if (this.navButtons) {
          // Update disabled state for prev button
          if (index === 0) {
            this.navButtons
              .querySelector('.nav-prev')
              .classList.add('opacity-50', 'cursor-not-allowed');
          } else {
            this.navButtons
              .querySelector('.nav-prev')
              .classList.remove('opacity-50', 'cursor-not-allowed');
          }

          // Update disabled state for next button
          if (index === this.childElements.length - 1) {
            this.navButtons
              .querySelector('.nav-next')
              .classList.add('opacity-50', 'cursor-not-allowed');
          } else {
            this.navButtons
              .querySelector('.nav-next')
              .classList.remove('opacity-50', 'cursor-not-allowed');
          }
        }
      }

      generateCarousel() {
        this.innerHTML = '';

        // Create containers for desktop and mobile layouts
        const desktopContainer = document.createElement('div');
        desktopContainer.classList.add(
          'hidden',
          'lg:flex',
          'lg:flex-row',
          'lg:gap-6'
        );

        const mobileContainer = document.createElement('div');
        mobileContainer.classList.add('block', 'lg:hidden', 'relative');

        // DESKTOP LAYOUT
        // --------------
        // Create left column for thumbnails
        const thumbnailColumn = document.createElement('div');
        thumbnailColumn.classList.add('w-12');

        // Create thumbnail elements for desktop
        const thumbnailList = document.createElement('div');
        thumbnailList.classList.add(
          'flex',
          'flex-col',
          'gap-1',
          'sticky',
          'mt-[18px]',
          'top-[190px]'
        );
        this.desktopThumbnails = thumbnailList;

        this.childElements.forEach((slide, index) => {
          const thumbSlide = slide.cloneNode(true);
          // Remove badges from thumbnails
          const badges = thumbSlide.querySelectorAll('.badge-text, .badge-image');
          badges.forEach((badge) => badge.remove());

          // Replace videos with their poster image in thumbnails
          const video = thumbSlide.querySelector('video');
          if (video) {
            const posterUrl = video.poster || '';
            const videoContainer = video.closest('.video-media-container');
            if (videoContainer && posterUrl) {
              // Create an image element to replace the video
              const img = document.createElement('img');
              img.src = posterUrl;
              img.className = 'h-full w-full object-cover';
              img.alt = video.getAttribute('aria-label') || 'Video thumbnail';

              // Replace the entire video container content
              videoContainer.innerHTML = '';
              videoContainer.appendChild(img);
            }
          }

          // Remove all existing classes that might interfere with sizing
          thumbSlide.className = '';

          thumbSlide.classList.add(
            'w-12',
            'h-12',
            'cursor-pointer',
            'thumbnail-item',
            'border',
            'transition-all',
            'overflow-hidden',
            'flex-shrink-0',
            index === 0 ? 'border-brand-secondary-200' : 'border-transparent'
          );

          thumbSlide.setAttribute('data-index', index);
          thumbSlide.addEventListener('click', () =>
            this.handleThumbnailClick(index)
          );

          thumbnailList.appendChild(thumbSlide);
        });

        thumbnailColumn.appendChild(thumbnailList);

        // Create right column for main image
        const mainImageColumn = document.createElement('div');
        mainImageColumn.classList.add('flex-grow', 'relative');

        // Create main image splide for desktop
        const desktopMainSlider = document.createElement('div');
        desktopMainSlider.classList.add('splide');

        const desktopMainTrack = document.createElement('div');
        desktopMainTrack.classList.add('splide__track');

        const desktopMainList = document.createElement('div');
        desktopMainList.classList.add('splide__list');
        desktopMainList.innerHTML = this.slides;

        desktopMainTrack.appendChild(desktopMainList);
        desktopMainSlider.appendChild(desktopMainTrack);

        // Add the slider to the main column
        mainImageColumn.appendChild(desktopMainSlider);

        // Create nav buttons for desktop
        const navButtons = document.createElement('div');
        navButtons.classList.add(
          'absolute',
          'bottom-[-40px]',
          'right-0',
          'transform',
          'flex',
          'flex-row',
          'gap-2',
          'z-10'
        );
        this.navButtons = navButtons;

        // Create prev button
        const prevButton = document.createElement('button');
        prevButton.classList.add(
          'nav-prev',
          'w-8',
          'h-8',
          'flex',
          'items-center',
          'justify-center',
          'bg-white',
          'hover:bg-brand-secondary-200',
          'hover:text-white',
          'transition-colors',
          'focus:outline-none',
          'opacity-50',
          'cursor-not-allowed' // Initially disabled since we start at the first slide
        );

        prevButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.4712 3.52835C10.2109 3.268 9.78878 3.268 9.52843 3.52835L5.52843 7.52835C5.26808 7.7887 5.26808 8.21081 5.52843 8.47116L9.52843 12.4712C9.78878 12.7315 10.2109 12.7315 10.4712 12.4712C10.7316 12.2108 10.7316 11.7887 10.4712 11.5283L6.94265 7.99975L10.4712 4.47116C10.7316 4.21081 10.7316 3.7887 10.4712 3.52835Z" fill="#2B2B2B"/>
          </svg>
        `;
        prevButton.addEventListener('click', () =>
          this.handleNavButtonClick('prev')
        );

        // Add SVG color change on hover
        prevButton.addEventListener('mouseenter', function () {
          const path = this.querySelector('path');
          if (path) path.setAttribute('fill', 'white');
        });

        prevButton.addEventListener('mouseleave', function () {
          const path = this.querySelector('path');
          if (path) path.setAttribute('fill', '#2B2B2B');
        });

        // Create next button
        const nextButton = document.createElement('button');
        nextButton.classList.add(
          'nav-next',
          'w-8',
          'h-8',
          'flex',
          'items-center',
          'justify-center',
          'bg-white',
          'hover:bg-brand-secondary-200',
          'hover:text-white',
          'transition-colors',
          'focus:outline-none'
        );
        // Here's your custom SVG
        nextButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52835C5.78911 3.268 6.21122 3.268 6.47157 3.52835L10.4716 7.52835C10.7319 7.7887 10.7319 8.21081 10.4716 8.47116L6.47157 12.4712C6.21122 12.7315 5.78911 12.7315 5.52876 12.4712C5.26841 12.2108 5.26841 11.7887 5.52876 11.5283L9.05735 7.99975L5.52876 4.47116C5.26841 4.21081 5.26841 3.7887 5.52876 3.52835Z" fill="#2B2B2B"/>
          </svg>
        `;
        nextButton.addEventListener('click', () =>
          this.handleNavButtonClick('next')
        );

        // Add SVG color change on hover
        nextButton.addEventListener('mouseenter', function () {
          const path = this.querySelector('path');
          if (path) path.setAttribute('fill', 'white');
        });

        nextButton.addEventListener('mouseleave', function () {
          const path = this.querySelector('path');
          if (path) path.setAttribute('fill', '#2B2B2B');
        });

        navButtons.appendChild(prevButton);
        navButtons.appendChild(nextButton);

        // Append the navigation buttons as a direct child of mainImageColumn
        mainImageColumn.appendChild(navButtons);

        // Add columns to desktop container
        desktopContainer.appendChild(thumbnailColumn);
        desktopContainer.appendChild(mainImageColumn);

        // MOBILE LAYOUT
        // -------------
        // Create main mobile carousel
        const mobileSlider = document.createElement('div');
        mobileSlider.classList.add('splide', 'mb-4');
        // Don't rely on ID selectors
        mobileSlider.classList.add('mobile-main-carousel');

        const mobileTrack = document.createElement('div');
        mobileTrack.classList.add('splide__track');

        const mobileList = document.createElement('div');
        mobileList.classList.add('splide__list');
        mobileList.innerHTML = this.slides;

        mobileTrack.appendChild(mobileList);
        mobileSlider.appendChild(mobileTrack);
        mobileContainer.appendChild(mobileSlider);

        // Variable to store mobile thumbnail slider reference
        let mobileThumbnailSlider;

        // Create mobile thumbnails at bottom (if enabled)
        if (this.useThumbnails) {
          mobileThumbnailSlider = document.createElement('div');
          mobileThumbnailSlider.classList.add('splide', 'splide--thumbnails');
          // Don't rely on ID selectors
          mobileThumbnailSlider.classList.add('mobile-thumbnail-carousel');

          const mobileThumbnailTrack = document.createElement('div');
          mobileThumbnailTrack.classList.add('splide__track');

          const mobileThumbnailList = document.createElement('div');
          mobileThumbnailList.classList.add('splide__list');

          // Store reference to mobile thumbnails list for active state management
          this.mobileThumbnails = mobileThumbnailList;

          // Create thumbnail slides
          const thumbnailSlides = [...this.childElements].map((slide) => {
            const thumbSlide = slide.cloneNode(true);
            // Remove badges from thumbnails
            const badges = thumbSlide.querySelectorAll('.badge-text, .badge-image');
            badges.forEach((badge) => badge.remove());

            // Replace videos with their poster image in thumbnails
            const video = thumbSlide.querySelector('video');
            if (video) {
              const posterUrl = video.poster || '';
              const videoContainer = video.closest('.video-media-container');
              if (videoContainer && posterUrl) {
                // Create an image element to replace the video
                const img = document.createElement('img');
                img.src = posterUrl;
                img.className = 'h-full w-full object-cover';
                img.alt = video.getAttribute('aria-label') || 'Video thumbnail';

                // Replace the entire video container content
                videoContainer.innerHTML = '';
                videoContainer.appendChild(img);
              }
            }

            return thumbSlide;
          });

          mobileThumbnailList.append(...thumbnailSlides);
          mobileThumbnailTrack.appendChild(mobileThumbnailList);
          mobileThumbnailSlider.appendChild(mobileThumbnailTrack);
          mobileContainer.appendChild(mobileThumbnailSlider);
        }

        // Add containers to component
        this.appendChild(desktopContainer);
        this.appendChild(mobileContainer);

        // Initialize Splide for desktop - pass the element directly
        const desktopMainSplide = new window.Splide(desktopMainSlider, {
          arrows: false,
          pagination: false,
          drag: true,
          perPage: 1
        });

        // Initialize Splide for mobile main carousel - pass the element directly
        const mobileMainSplide = new window.Splide(mobileSlider, {
          arrows: false,
          pagination: false,
          perPage: 1,
          classes: {
            pagination:
              'splide__pagination media__pagination absolute bottom-4 left-1/2 transform -translate-x-1/2',
            page: 'splide__pagination__page media-page bg-white border border-brand-secondary-200 w-2 h-2 mx-1'
          }
        });

        // Initialize Splide for mobile thumbnails if thumbnails are enabled
        let mobileThumbnailSplide;
        if (this.useThumbnails && mobileThumbnailSlider) {
          mobileThumbnailSplide = new window.Splide(mobileThumbnailSlider, {
            arrows: false,
            pagination: false,
            perPage: 5,
            gap: 10,
            rewind: true,
            isNavigation: true,
            breakpoints: {
              640: {
                perPage: 7
              }
            },
            fixedHeight: 48,
            fixedWidth: 48
          });

          // Sync main and thumbnail carousels for mobile
          mobileMainSplide.sync(mobileThumbnailSplide);
          // Note: Mount happens later after event listeners are set up
        }

        // Add event listener to desktop main splide to update thumbnail active state
        desktopMainSplide.on('moved', (newIndex) => {
          this.updateActiveState(newIndex);
        });

        // Add event listener to mobile main splide to update thumbnail active state
        mobileMainSplide.on('moved', (newIndex) => {
          this.updateActiveState(newIndex);
        });

        // Handle video playback on slide change for desktop
        desktopMainSplide.on('active', (Slide) => {
          // Pause all videos when switching slides
          const allVideos = this.querySelectorAll('video');
          allVideos.forEach(video => {
            if (!video.paused) {
              video.pause();
            }
          });

          // Handle current slide video - Slide.slide is the DOM element
          const currentVideo = Slide?.slide?.querySelector('video');
          if (currentVideo) {
            const container = currentVideo.closest('.video-media-container');
            const isSilent = container?.dataset.isSilent === 'true';

            // Only autoplay silent videos (those marked as silent)
            if (isSilent) {
              currentVideo.play().catch(() => {
                // Fail silently if autoplay is blocked by browser
              });
            }
            // Videos with controls require user interaction to play
          }
        });

        // Handle video playback on slide change for mobile
        mobileMainSplide.on('active', (Slide) => {
          // Pause all videos when switching slides
          const allVideos = this.querySelectorAll('video');
          allVideos.forEach(video => {
            if (!video.paused) {
              video.pause();
            }
          });

          // Handle current slide video - Slide.slide is the DOM element
          const currentVideo = Slide?.slide?.querySelector('video');
          if (currentVideo) {
            const container = currentVideo.closest('.video-media-container');
            const isSilent = container?.dataset.isSilent === 'true';

            // Only autoplay silent videos (those marked as silent)
            if (isSilent) {
              currentVideo.play().catch(() => {
                // Fail silently if autoplay is blocked by browser
              });
            }
            // Videos with controls require user interaction to play
          }
        });

        // Store main splide reference for thumbnail click handler
        this.mainSplide = desktopMainSplide;

        // Mount splides - main carousels first, then synced thumbnails
        desktopMainSplide.mount();
        mobileMainSplide.mount();

        // Mount mobile thumbnails after main carousel is mounted (required for sync)
        if (this.useThumbnails && mobileThumbnailSplide) {
          mobileThumbnailSplide.mount();
        }

        // Set initial state for nav buttons
        this.updateActiveState(0);
      }
    }
  );
}
